import React, { useState, useEffect } from 'react';
import HeadingBanner from "../Components/HeadingBanner.jsx";
import Dashboard from "../Components/Dashboard.jsx";
import axios from "axios";
import toast from 'react-hot-toast';
import { Spinner, Modal } from 'react-bootstrap';
import { PlusCircle, Trash, Eye, X } from 'react-bootstrap-icons';

const ManufacturerDesigns = ({ settings }) => {
    const [designs, setDesigns] = useState([
        { id: 1, productName: '', description: '', grams: '', quantity: 1, unit: 'Grams', image: null, preview: null }
    ]);
    const [submittedDesigns, setSubmittedDesigns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const baseUrl = process.env.REACT_APP_BASE_URL || 'https://admin.greenheapgold.com/public/api/v1/';
    const adminUrl = baseUrl.replace('/api/v1/', '/');

    const fetchDesignsHistory = async () => {
        setLoading(true);
        const token = localStorage.getItem('token');
        try {
            const res = await axios.get(`${baseUrl}manufacturer_designs/list`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data && res.data.status) {
                setSubmittedDesigns(res.data.data || []);
            }
        } catch (err) {
            console.error('Error fetching designs:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDesignsHistory();
    }, []);

    const handleAddDesign = () => {
        setDesigns(prev => [
            ...prev,
            { id: Date.now(), productName: '', description: '', grams: '', quantity: 1, unit: 'Grams', image: null, preview: null }
        ]);
    };

    const handleRemoveDesign = (id) => {
        if (designs.length === 1) {
            toast.error('At least one design form is required.');
            return;
        }
        setDesigns(prev => prev.filter(item => item.id !== id));
    };

    const handleInputChange = (id, field, value) => {
        setDesigns(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
    };

    const handleImageChange = (id, file) => {
        if (file) {
            const previewUrl = URL.createObjectURL(file);
            setDesigns(prev => prev.map(item => item.id === id ? { ...item, image: file, preview: previewUrl } : item));
        }
    };

    const handleSubmit = async (isDraft = false) => {
        // Validate items
        const invalid = designs.some(d => !d.productName.trim() || !d.image || !d.grams);
        if (invalid) {
            toast.error('Please enter Product Name, Grams, and upload an Image for all designs.');
            return;
        }

        setSubmitting(true);
        const token = localStorage.getItem('token');
        const formData = new FormData();

        if (isDraft) {
            formData.append('is_draft', '1');
        }

        designs.forEach((item, index) => {
            formData.append(`product_name[${index}]`, item.productName);
            formData.append(`product_description[${index}]`, item.description);
            formData.append(`grams[${index}]`, item.grams);
            formData.append(`quantity[${index}]`, item.quantity);
            formData.append(`unit[${index}]`, item.unit);
            if (item.image) {
                formData.append(`product_image[${index}]`, item.image);
            }
        });

        try {
            const res = await axios.post(`${baseUrl}manufacturer_designs/submit`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (res.data && res.data.status) {
                toast.success(res.data.message || 'Designs submitted successfully.');
                setDesigns([
                    { id: Date.now(), productName: '', description: '', grams: '', quantity: 1, unit: 'Grams', image: null, preview: null }
                ]);
                fetchDesignsHistory();
            } else {
                toast.error(res.data?.message || 'Failed to submit designs.');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error submitting designs.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <HeadingBanner title={"Manufacturer / Company Designs"} />
            <section className="py-4">
                <div className="container">
                    <div className="row">
                        <div className="col-xl-3 col-lg-4 mb-4">
                            <Dashboard setting_data={settings} />
                        </div>
                        <div className="col-xl-9 col-lg-8">
                            <div className="bg-white p-4 rounded shadow-sm border">
                                <h4 className="fw-bold mb-3 border-bottom pb-2">
                                    Upload Product Designs
                                </h4>

                                {/* Multi-Design Dynamic Form */}
                                <div className="mb-4">
                                    {designs.map((design, index) => (
                                        <div key={design.id} className="p-3 mb-3 border rounded bg-light position-relative">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <h6 className="fw-bold text-primary mb-0">Design #{index + 1}</h6>
                                                {designs.length > 1 && (
                                                    <button 
                                                        type="button" 
                                                        className="btn btn-sm btn-outline-danger py-0 px-2"
                                                        onClick={() => handleRemoveDesign(design.id)}
                                                    >
                                                        <Trash className="me-1" /> Remove
                                                    </button>
                                                )}
                                            </div>

                                            <div className="row g-3">
                                                <div className="col-md-3 text-center">
                                                    <label className="form-label fw-semibold fs-7 d-block">
                                                        Product Image <span className="text-danger">*</span>
                                                    </label>
                                                    <div className="border rounded bg-white p-2 text-center" style={{ minHeight: '120px' }}>
                                                        {design.preview ? (
                                                            <img src={design.preview} alt="Preview" className="img-fluid rounded" style={{ maxHeight: '100px', objectFit: 'contain' }} />
                                                        ) : (
                                                            <div className="text-muted fs-8 py-4">No image selected</div>
                                                        )}
                                                    </div>
                                                    <input 
                                                        type="file" 
                                                        accept="image/*" 
                                                        className="form-control form-control-sm mt-2" 
                                                        onChange={(e) => handleImageChange(design.id, e.target.files[0])}
                                                        required
                                                    />
                                                </div>

                                                <div className="col-md-9">
                                                    <div className="row g-2">
                                                        <div className="col-md-6">
                                                            <label className="form-label fw-semibold fs-7">Product Name <span className="text-danger">*</span></label>
                                                            <input 
                                                                type="text" 
                                                                className="form-control form-control-sm" 
                                                                placeholder="e.g. Gold Necklace Design A1"
                                                                value={design.productName}
                                                                onChange={(e) => handleInputChange(design.id, 'productName', e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                        <div className="col-md-3">
                                                            <label className="form-label fw-semibold fs-7">Weight / Grams <span className="text-danger">*</span></label>
                                                            <input 
                                                                type="number" 
                                                                step="0.01"
                                                                className="form-control form-control-sm" 
                                                                placeholder="e.g. 12.50"
                                                                value={design.grams}
                                                                onChange={(e) => handleInputChange(design.id, 'grams', e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                        <div className="col-md-3">
                                                            <label className="form-label fw-semibold fs-7">Quantity & Unit</label>
                                                            <div className="input-group input-group-sm">
                                                                <input 
                                                                    type="number" 
                                                                    className="form-control" 
                                                                    value={design.quantity}
                                                                    onChange={(e) => handleInputChange(design.id, 'quantity', e.target.value)}
                                                                />
                                                                <select 
                                                                    className="form-select form-select-sm"
                                                                    value={design.unit}
                                                                    onChange={(e) => handleInputChange(design.id, 'unit', e.target.value)}
                                                                >
                                                                    <option value="Grams">Grams</option>
                                                                    <option value="Pieces">Pieces</option>
                                                                    <option value="Kg">Kg</option>
                                                                    <option value="Sets">Sets</option>
                                                                    <option value="Pair">Pair</option>
                                                                </select>
                                                            </div>
                                                        </div>

                                                        <div className="col-md-12 mt-2">
                                                            <label className="form-label fw-semibold fs-7">Product Description</label>
                                                            <textarea 
                                                                className="form-control form-control-sm" 
                                                                rows="2" 
                                                                placeholder="Enter product details, purity, specifications..."
                                                                value={design.description}
                                                                onChange={(e) => handleInputChange(design.id, 'description', e.target.value)}
                                                            ></textarea>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <button 
                                            type="button" 
                                            className="btn btn-outline-primary btn-sm"
                                            onClick={handleAddDesign}
                                        >
                                            <PlusCircle className="me-1" /> + Add Design
                                        </button>

                                        <div className="d-flex gap-2">
                                            <button 
                                                type="button" 
                                                className="btn btn-secondary btn-sm"
                                                onClick={() => handleSubmit(true)}
                                                disabled={submitting}
                                            >
                                                Save Draft
                                            </button>
                                            <button 
                                                type="button" 
                                                className="btn btn-primary btn-sm"
                                                onClick={() => handleSubmit(false)}
                                                disabled={submitting}
                                            >
                                                {submitting ? <Spinner animation="border" size="sm" /> : 'Submit Designs'}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Design Status & History Section */}
                                <div className="mt-5 border-top pt-4">
                                    <h4 className="fw-bold mb-3">Submitted Designs History</h4>


                                    {loading ? (
                                        <div className="text-center py-4"><Spinner animation="border" /></div>
                                    ) : submittedDesigns.length > 0 ? (
                                        <div className="row g-3">
                                            {submittedDesigns.map((item) => {
                                                const status = item.status || 'Pending';
                                                let badgeVariant = 'warning';
                                                let badgeText = 'Pending Approval';

                                                if (status === 'Approved') {
                                                    badgeVariant = 'success';
                                                    badgeText = 'Approved';
                                                } else if (status === 'Rejected') {
                                                    badgeVariant = 'danger';
                                                    badgeText = 'Rejected';
                                                } else if (status === 'Draft') {
                                                    badgeVariant = 'secondary';
                                                    badgeText = 'Draft';
                                                }

                                                return (
                                                    <div key={item.id} className="col-md-6">
                                                        <div className="border rounded p-3 bg-white h-100 shadow-sm d-flex flex-column justify-content-between">
                                                            <div>
                                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                                    <h6 className="fw-bold text-dark mb-0">{item.product_name}</h6>
                                                                    <span className={`badge bg-${badgeVariant} fs-7`}>
                                                                        {badgeText}
                                                                    </span>
                                                                </div>

                                                                <div className="d-flex gap-3 my-2">
                                                                    <img 
                                                                        src={`${adminUrl}manufacturer_designs/${item.product_image}`} 
                                                                        alt={item.product_name} 
                                                                        className="rounded border" 
                                                                        style={{ width: '80px', height: '80px', objectFit: 'contain', cursor: 'pointer' }}
                                                                        onClick={() => {
                                                                            setPreviewImage(`${adminUrl}manufacturer_designs/${item.product_image}`);
                                                                            setShowModal(true);
                                                                        }}
                                                                    />
                                                                    <div className="fs-7 text-muted">
                                                                        <div><strong>Weight:</strong> {item.grams} g</div>
                                                                        <div><strong>Quantity:</strong> {item.quantity} {item.unit}</div>
                                                                        <div><strong>Submitted:</strong> {new Date(item.created_at).toLocaleDateString()}</div>
                                                                    </div>
                                                                </div>

                                                                <p className="fs-8 text-secondary mb-2">
                                                                    {item.product_description || 'No description provided.'}
                                                                </p>

                                                                {status === 'Rejected' && item.rejection_reason && (
                                                                    <div className="alert alert-danger py-1 px-2 fs-8 mb-2">
                                                                        <strong>Reason:</strong> {item.rejection_reason}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div className="text-end border-top pt-2 mt-2">
                                                                <button 
                                                                    type="button" 
                                                                    className="btn btn-sm btn-outline-secondary"
                                                                    onClick={() => {
                                                                        setPreviewImage(`${adminUrl}manufacturer_designs/${item.product_image}`);
                                                                        setShowModal(true);
                                                                    }}
                                                                >
                                                                    <Eye me-1 /> View Full Image
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="alert alert-info text-center py-4">
                                            No designs submitted yet. Add your designs above and click Submit Designs.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Lightbox Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">

                <Modal.Header closeButton>
                    <Modal.Title className="fs-6">Design Image Preview</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    {previewImage && (
                        <img src={previewImage} alt="Preview" className="img-fluid rounded" style={{ maxHeight: '650px' }} />
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

export default ManufacturerDesigns;
